// Keeps the launcher away from other fixed UI of the host page (chat bubbles, cookie
// banners, accessibility buttons, sticky footers). Regular scrolling content is not
// treated as a collision – a corner launcher always floats above some content.
import type { Corner } from '../shared/types.ts';

export interface Spot {
  position: Corner;
  offsetX: number;
  offsetY: number;
}

export interface Size {
  width: number;
  height: number;
}

const STEP = 72;

function mirrorX(position: Corner): Corner {
  return position.endsWith('right') ? (position.replace('right', 'left') as Corner) : (position.replace('left', 'right') as Corner);
}

function mirrorY(position: Corner): Corner {
  return position.startsWith('bottom') ? (position.replace('bottom', 'top') as Corner) : (position.replace('top', 'bottom') as Corner);
}

/** Candidate spots in order of preference: preferred corner, shifted along the edge, other corners. */
export function candidates(preferred: Spot): Spot[] {
  const { position, offsetX, offsetY } = preferred;
  const list: Spot[] = [];
  for (const pos of [position, mirrorX(position), mirrorY(position), mirrorY(mirrorX(position))]) {
    for (let i = 0; i < 3; i++) list.push({ position: pos, offsetX, offsetY: offsetY + i * STEP });
  }
  return list;
}

export function spotRect(spot: Spot, size: Size, viewport: Size): DOMRect {
  const x = spot.position.endsWith('right') ? viewport.width - spot.offsetX - size.width : spot.offsetX;
  const y = spot.position.startsWith('bottom') ? viewport.height - spot.offsetY - size.height : spot.offsetY;
  return new DOMRect(x, y, size.width, size.height);
}

/** True if the element (or an ancestor) is fixed/sticky, i.e. belongs to an overlay layer of the page. */
function isFloatingLayer(el: Element): boolean {
  let current: Element | null = el;
  for (let depth = 0; current && depth < 25; depth++) {
    if (current === document.body || current === document.documentElement) return false;
    const position = getComputedStyle(current).position;
    if (position === 'fixed' || position === 'sticky') return true;
    current = current.parentElement ?? ((current.getRootNode() as ShadowRoot).host ?? null);
  }
  return false;
}

// Content worth keeping clear when the page itself does not scroll (kiosk/beamer views, app shells).
const MEANINGFUL = 'a[href], button, input, select, textarea, label, summary, img, svg, canvas, video, iframe, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';

function pageScrolls(): boolean {
  const scroller = document.scrollingElement ?? document.documentElement;
  return scroller.scrollHeight > window.innerHeight + 1;
}

export function collides(rect: DOMRect, ownHost: Element): boolean {
  // On a scrolling page everything moves under the launcher anyway; on a static page the
  // layout is fixed, so controls and images (e.g. a QR code on a beamer view) must stay visible.
  const staticPage = !pageScrolls();
  const inset = 4;
  const points: Array<[number, number]> = [
    [rect.left + rect.width / 2, rect.top + rect.height / 2],
    [rect.left + inset, rect.top + inset],
    [rect.right - inset, rect.top + inset],
    [rect.left + inset, rect.bottom - inset],
    [rect.right - inset, rect.bottom - inset],
  ];
  for (const [x, y] of points) {
    if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return true;
    const hit = document.elementsFromPoint(x, y).find((el) => el !== ownHost);
    if (hit && (isFloatingLayer(hit) || (staticPage && hit.closest(MEANINGFUL)))) return true;
  }
  return false;
}

type Rgba = [number, number, number, number];

function parseColor(value: string): Rgba | undefined {
  const rgb = /^rgba?\(([^)]+)\)$/.exec(value);
  const srgb = /^color\(srgb ([^)]+)\)$/.exec(value);
  const parts = (rgb?.[1] ?? srgb?.[1])?.split(/[\s,/]+/).filter(Boolean).map(Number);
  if (!parts || parts.length < 3 || parts.some(Number.isNaN)) return undefined;
  const scale = srgb ? 255 : 1;
  return [(parts[0] as number) * scale, (parts[1] as number) * scale, (parts[2] as number) * scale, parts[3] ?? 1];
}

function luminance([r, g, b]: Rgba): number {
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const LUMINANCE_TANNE = luminance([0, 84, 55, 1]);
const LUMINANCE_SONNE = luminance([255, 241, 122, 1]);

/**
 * True if the page behind the point is so dark that a Sonne-coloured launcher stands out
 * better than the Tanne one (e.g. tools that use Tanne as their own background).
 */
export function prefersLightLauncher(x: number, y: number, ownHost: Element): boolean {
  let el: Element | null = document.elementsFromPoint(x, y).find((e) => e !== ownHost) ?? document.body;
  for (let depth = 0; el && depth < 40; depth++) {
    const color = parseColor(getComputedStyle(el).backgroundColor);
    if (color && color[3] >= 0.5) {
      const bg = luminance(color);
      return contrast(LUMINANCE_SONNE, bg) > contrast(LUMINANCE_TANNE, bg);
    }
    el = el.parentElement;
  }
  return false;
}

export function findFreeSpot(preferred: Spot, size: Size, ownHost: Element): Spot {
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  for (const spot of candidates(preferred)) {
    const rect = spotRect(spot, size, viewport);
    // Shifted spots can leave the viewport on small screens.
    if (rect.top < 0 || rect.bottom > viewport.height) continue;
    if (!collides(rect, ownHost)) return spot;
  }
  return preferred;
}
